import { EmbarkCart } from 'shopify-buy';
import { getOrCreateCart } from '../storefront-cart';
import { useEffect, useState } from 'preact/hooks';

import { DrawerBody } from './DrawerBody';
import { DrawerFooter } from './DrawerFooter';
import { DrawerHeader } from './DrawerHeader';
import { EmptyCart } from './EmptyCart';
import { FlagContext } from './Context/flag';
import React from 'preact/compat';
import { notifyHandledException } from '../../utils/errorLogger';

type ValueProp = {
  value: string;
  show?: ((cart: EmbarkCart) => boolean) | boolean;
};

export function getValProps(cart: EmbarkCart | null): Array<string> {
  const valuePropsList: Array<ValueProp> = [
    {
      value: 'Free Shipping in the US',
    },
    {
      value: 'Interest-free financing available',
    },
  ];

  const showableValueProps = (value: ValueProp): boolean => {
    if (!cart) {
      return true;
    }

    if (typeof value.show === 'function') {
      return value.show(cart);
    }

    if (typeof value.show === 'boolean') {
      return value.show;
    }

    return true;
  };

  return valuePropsList.filter(showableValueProps).map((v) => v.value);
}

export const Drawer: React.FC = () => {
  const [isOpen, _setIsOpen] = useState(false);
  const [cartData, setCartData] = useState<EmbarkCart | null>(null);
  const [valueProps, setValueProps] = useState<string[]>([]);

  const setIsOpen = (isOpen: boolean): void => {
    if (isOpen) {
      fetchItems().catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error fetching cart items:', error);
        notifyHandledException(error);
      });
    }
    _setIsOpen(isOpen);
  };

  const fetchItems = async (cart: EmbarkCart | null = null): Promise<void> => {
    if (!cart) {
      cart = await getOrCreateCart();
    }

    if (!cart) {
      return;
    }

    setCartData(cart);

    // announce cart updates to outside/other parts of the shopify theme.
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          cart,
        },
      })
    );
  };

  const toggleDrawer = (): void => {
    setIsOpen(!isOpen);

    if (isOpen) {
      document.dispatchEvent(
        new CustomEvent('cart-drawer:closed', {
          detail: {
            cart: cartData,
          },
        })
      );
    } else {
      document.dispatchEvent(
        new CustomEvent('cart-drawer:opened', {
          detail: {
            cart: cartData,
          },
        })
      );
    }
  };

  const open = (): void => {
    setIsOpen(true);
    document.dispatchEvent(
      new CustomEvent('cart-drawer:opened', {
        detail: {
          cart: cartData,
        },
      })
    );
  };

  const close = (): void => {
    setIsOpen(false);
    document.dispatchEvent(
      new CustomEvent('cart-drawer:closed', {
        detail: {
          cart: cartData,
        },
      })
    );
  };

  useEffect(() => {
    fetchItems(cartData).catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Error fetching cart items:', error);
      notifyHandledException(error);
    });

    document.addEventListener('cart-drawer:toggle', toggleDrawer);
    document.addEventListener('cart-drawer:open', open);
    document.addEventListener('cart-drawer:close', close);

    return () => {
      document.removeEventListener('cart-drawer:toggle', toggleDrawer);
      document.removeEventListener('cart-drawer:open', open);
      document.removeEventListener('cart-drawer:close', close);
    };
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('cart') === 'open') {
      open();
    }
  }, []);

  useEffect(() => {
    if (cartData) {
      const valProps = getValProps(cartData);
      setValueProps(valProps);
    }
  }, [cartData]);

  return (
    <FlagContext.Provider value={{}}>
      <div>
        {isOpen && (
          <div
            onClick={() => setIsOpen(false)}
            className="cart-drawer-overlay"
          ></div>
        )}
        <div
          className={`cart-drawer ${
            isOpen ? 'cart-drawer--open' : 'cart-drawer--closed'
          }`}
          aria-label={'Cart Modal'}
          aria-modal={'true'}
          role={'dialog'}
        >
          <DrawerHeader cart={cartData} toggleDrawer={toggleDrawer} />
          {!cartData?.lines?.edges.length ? (
            <EmptyCart />
          ) : (
            <>
              <DrawerBody
                cart={cartData}
                fetchItems={fetchItems}
                valueProps={valueProps}
              />
              <DrawerFooter cart={cartData} />
            </>
          )}
        </div>
      </div>
    </FlagContext.Provider>
  );
};
